import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Distillation lifecycle. `processing` is the only animated one — the row
// pulses softly while the OCR + chunk + LLM-summarise pipeline runs in the
// background. `distilled` shows the extracted-notes count; `error` shows
// the failure message in a tooltip on the warn icon.
const STATUS = {
  processing: { cls: 'doc-status-processing', label: 'در حال پردازش' },
  distilled:  { cls: 'doc-status-distilled',  label: 'تقطیر شد' },
  error:      { cls: 'doc-status-error',      label: 'خطا' },
};

// Per-file-type accent. Tints the left edge of each card + the document
// icon's stripe. Keeps a quick visual scan possible when the list is long.
const TYPE_ACCENT = {
  pdf:  { color: '#dc2626', label: 'PDF',  bg: 'rgba(220, 38, 38, .08)'  },
  docx: { color: '#2563eb', label: 'DOCX', bg: 'rgba(37, 99, 235, .08)'  },
  doc:  { color: '#2563eb', label: 'DOC',  bg: 'rgba(37, 99, 235, .08)'  },
  txt:  { color: '#475569', label: 'TXT',  bg: 'rgba(71, 85, 105, .08)'  },
  jpg:  { color: '#10b981', label: 'JPG',  bg: 'rgba(16, 185, 129, .08)' },
  jpeg: { color: '#10b981', label: 'JPG',  bg: 'rgba(16, 185, 129, .08)' },
  png:  { color: '#10b981', label: 'PNG',  bg: 'rgba(16, 185, 129, .08)' },
  webp: { color: '#10b981', label: 'WEBP', bg: 'rgba(16, 185, 129, .08)' },
  mp3:  { color: '#f59e0b', label: 'MP3',  bg: 'rgba(245, 158, 11, .08)' },
  wav:  { color: '#f59e0b', label: 'WAV',  bg: 'rgba(245, 158, 11, .08)' },
  ogg:  { color: '#f59e0b', label: 'OGG',  bg: 'rgba(245, 158, 11, .08)' },
  mp4:  { color: '#a855f7', label: 'MP4',  bg: 'rgba(168, 85, 247, .08)' },
  mov:  { color: '#a855f7', label: 'MOV',  bg: 'rgba(168, 85, 247, .08)' },
  _default: { color: '#64748b', label: 'FILE', bg: 'rgba(100, 116, 139, .08)' },
};

const ACCEPT_EXTS = ['pdf', 'docx', 'doc', 'txt', 'jpg', 'jpeg', 'png', 'webp', 'mp3', 'wav', 'ogg', 'mp4', 'mov'];
const ACCEPT_ATTR = '.pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.webp,.mp3,.wav,.ogg,.mp4,.mov';
// 18 MB — base64-encoding a 25 MB file balloons to ~33 MB on the wire, which can
// trip OpenRouter's ~25 MB inline-multimodal payload cap. 18 MB encodes to ~24 MB,
// leaving a safety margin for the rest of the request body.
const MAX_BYTES = 18 * 1024 * 1024;

function extOf(name) {
  const m = /\.([^.]+)$/.exec(name || '');
  return m ? m[1].toLowerCase() : '';
}
function accentFor(name) {
  return TYPE_ACCENT[extOf(name)] || TYPE_ACCENT._default;
}
function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024)                  return `${n} B`;
  if (n < 1024 * 1024)           return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024)    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fa-IR'); }
  catch { return ''; }
}

// ─── Tiny SVG library — kept inline so each glyph reads as part of the layout
//     rather than a plug-in icon set. All strokes scale crisply at any size.
const UploadGlyph = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <defs>
      <linearGradient id="docu-upload-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"  stopColor="#6366f1" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
    </defs>
    <rect x="14" y="20" width="36" height="32" rx="5"
          fill="url(#docu-upload-grad)" opacity=".10" />
    <rect x="14" y="20" width="36" height="32" rx="5"
          fill="none" stroke="url(#docu-upload-grad)" strokeWidth="1.6" />
    <path d="M32 38 V18 M24 26 L32 18 L40 26"
          fill="none" stroke="url(#docu-upload-grad)"
          strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="32" cy="46" r="1.6" fill="url(#docu-upload-grad)" />
  </svg>
);

function FileGlyph({ ext, color }) {
  return (
    <svg viewBox="0 0 40 48" aria-hidden="true" className="doc-file-glyph">
      <path
        d="M6 4 a3 3 0 0 1 3 -3 h18 l9 9 v34 a3 3 0 0 1 -3 3 h-24 a3 3 0 0 1 -3 -3 z"
        fill="#fff" stroke={color} strokeWidth="1.5"
      />
      <path d="M27 1 v6 a3 3 0 0 0 3 3 h6" fill="none" stroke={color} strokeWidth="1.5" />
      <rect x="9" y="32" width="22" height="2.4" rx="1.2" fill={color} opacity=".24" />
      <rect x="9" y="37" width="14" height="2.4" rx="1.2" fill={color} opacity=".24" />
      <text x="20" y="25" textAnchor="middle"
            fontSize="7.5" fontWeight="700" fill={color}
            fontFamily="'JetBrains Mono', ui-monospace, monospace">
        {ext}
      </text>
    </svg>
  );
}

const EmptyState = () => (
  <div className="docu-empty">
    <svg viewBox="0 0 96 72" aria-hidden="true" className="docu-empty-art">
      <defs>
        <linearGradient id="docu-empty-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor="#e0e7ff" />
          <stop offset="100%" stopColor="#ede9fe" />
        </linearGradient>
      </defs>
      <rect x="14" y="14" width="42" height="52" rx="4"
            fill="url(#docu-empty-grad)" stroke="#c7d2fe" strokeWidth="1" />
      <rect x="30" y="6" width="42" height="52" rx="4"
            fill="#fff" stroke="#a5b4fc" strokeWidth="1.2" />
      <rect x="38" y="22" width="26" height="2" rx="1" fill="#c7d2fe" />
      <rect x="38" y="28" width="22" height="2" rx="1" fill="#c7d2fe" />
      <rect x="38" y="34" width="18" height="2" rx="1" fill="#c7d2fe" />
    </svg>
    <span className="docu-empty-text">هنوز سندی بارگذاری نشده</span>
    <span className="docu-empty-hint">PDF · DOCX · تصویر · صدا · ویدئو را بکش و رها کن - تقطیر چندحالته خودکار شروع می‌شود.</span>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────
export default function DocumentsPanel({ agent }) {
  const fileRef = useRef(null);
  const [isDragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Mount once + poll every 5s while panel is open so `processing` rows
  // promote to `distilled` without user clicking refresh.
  useEffect(() => {
    agent.loadDocuments();
    const t = setInterval(() => { agent.loadDocuments(); }, 5000);
    return () => clearInterval(t);
  }, [agent.loadDocuments]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateAndUpload = useCallback(async (file) => {
    setLocalError(null);
    if (!file) return;
    const ext = extOf(file.name);
    if (!ACCEPT_EXTS.includes(ext)) {
      setLocalError(`نوع فایل پشتیبانی نمی‌شود (.${ext || '?'} ). فقط ${ACCEPT_EXTS.join(' / ')} مجاز است.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(`فایل خیلی بزرگ است (${formatBytes(file.size)}). حداکثر ${formatBytes(MAX_BYTES)}.`);
      return;
    }
    await agent.uploadDocument(file);
  }, [agent]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (file) await validateAndUpload(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Drag-and-drop. We only count drags that carry files (dragenter on a
  // selected-text drag would otherwise toggle the highlight).
  const carriesFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDragOver = (e) => {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    if (!isDragging) setDragging(true);
  };
  const onDragLeave = (e) => {
    // Only clear when leaving the dropzone itself, not its children.
    if (e.currentTarget === e.target) setDragging(false);
  };
  const onDrop = async (e) => {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await validateAndUpload(file);
  };

  // Total-distilled counter for the header.
  const stats = useMemo(() => {
    const total = agent.documents.length;
    const distilled = agent.documents.filter((d) => d.status === 'distilled').length;
    const notes = agent.documents.reduce((s, d) => s + (d.notes_extracted || 0), 0);
    return { total, distilled, notes };
  }, [agent.documents]);

  const error = localError || agent.error;

  return (
    <div className="consultant-panel docu-panel">
      <div className="docu-header">
        <div>
          <h2 className="docu-title">اسناد پایگاه دانش</h2>
          <p className="docu-subtitle">
            فایل را اینجا رها کن یا انتخاب کن. متن استخراج می‌شود، به جملات تبدیل می‌شود،
            و هر جمله به‌عنوان یک نتِ مستقل وارد پایگاه دانش می‌شود.
          </p>
        </div>
        {stats.total > 0 && (
          <div className="docu-stats" aria-label="آمار اسناد">
            <span className="docu-stat">
              <strong>{stats.distilled.toLocaleString('fa-IR')}</strong>
              <em>تقطیر‌شده</em>
            </span>
            <span className="docu-stat-divider" aria-hidden="true" />
            <span className="docu-stat">
              <strong>{stats.notes.toLocaleString('fa-IR')}</strong>
              <em>نتِ استخراج‌شده</em>
            </span>
          </div>
        )}
      </div>

      <label
        className={`docu-dropzone${isDragging ? ' is-dragging' : ''}${agent.busy ? ' is-busy' : ''}`}
        onDragEnter={onDragOver}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={fileRef} type="file" accept={ACCEPT_ATTR}
          onChange={onPick} disabled={agent.busy}
          className="docu-dropzone-input"
        />
        <div className="docu-dropzone-aurora" aria-hidden="true" />
        <div className="docu-dropzone-inner">
          <div className="docu-dropzone-icon"><UploadGlyph /></div>
          <div className="docu-dropzone-text">
            <strong>
              {agent.busy
                ? 'در حال بارگذاری روی فضای ذخیره‌سازی…'
                : isDragging
                  ? 'بشاش، فایلت رو رها کن'
                  : 'سندی را اینجا رها کن یا برای انتخاب کلیک کن'}
            </strong>
            <span className="docu-dropzone-meta">
              PDF · DOCX · TXT · تصویر · صدا · ویدئو
              {'  ·  '}حداکثر {formatBytes(MAX_BYTES)}
            </span>
          </div>
          <span className="docu-dropzone-cta" aria-hidden="true">
            {agent.busy ? <Spinner /> : 'انتخاب فایل'}
          </span>
        </div>
      </label>

      {error && (
        <div className="docu-error" role="alert" onClick={() => { setLocalError(null); agent.clearError?.(); }}>
          {error}
        </div>
      )}

      <div className="docu-list">
        {agent.documents.length === 0
          ? <EmptyState />
          : agent.documents.map((d, idx) => {
              const accent = accentFor(d.file_name);
              const s = STATUS[d.status] || { cls: '', label: d.status };
              return (
                <div
                  className={`docu-card ${s.cls}`}
                  key={d.id}
                  style={{
                    '--docu-accent': accent.color,
                    '--docu-accent-bg': accent.bg,
                    animationDelay: `${Math.min(idx, 8) * 40}ms`,
                  }}
                >
                  <div className="docu-card-icon">
                    <FileGlyph ext={accent.label} color={accent.color} />
                  </div>
                  <div className="docu-card-body">
                    <div className="docu-card-name" title={d.file_name}>{d.file_name}</div>
                    <div className="docu-card-meta">
                      {d.created_at && <span>{formatDate(d.created_at)}</span>}
                      {d.file_size && (
                        <>
                          <span className="docu-card-dot" aria-hidden="true" />
                          <span dir="ltr" className="docu-card-size">{formatBytes(d.file_size)}</span>
                        </>
                      )}
                      {d.status === 'distilled' && (
                        <>
                          <span className="docu-card-dot" aria-hidden="true" />
                          <span className="docu-card-notes">
                            {d.notes_extracted?.toLocaleString('fa-IR') || 0} نت
                          </span>
                        </>
                      )}
                      {d.status === 'error' && d.error_message && (
                        <>
                          <span className="docu-card-dot" aria-hidden="true" />
                          <span className="docu-card-err" title={d.error_message}>
                            {d.error_message.length > 60
                              ? d.error_message.slice(0, 60) + '…'
                              : d.error_message}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className={`docu-pill ${s.cls}`}>
                    {d.status === 'processing' && <Spinner small />}
                    {s.label}
                  </span>
                </div>
              );
            })}
      </div>
    </div>
  );
}

function Spinner({ small }) {
  return (
    <svg
      className={`docu-spinner${small ? ' is-small' : ''}`}
      viewBox="0 0 24 24" aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor"
              strokeWidth="2.4" strokeLinecap="round" strokeDasharray="14 36" />
    </svg>
  );
}
